import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Badge from '../UI/Badge';

export default function AssignmentList({ userRole }) {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (courseId) {
      fetchAssignments();
    }
  }, [courseId]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/assignments/course/${courseId}`);
      setAssignments(response.data.assignments || []);
    } catch (err) {
      setError(err.response?.data?.error || '과제 목록을 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (assignment) => {
    const now = new Date();
    const dueDate = new Date(assignment.dueDate);

    if (!assignment.dueDate) {
      return <Badge variant="default">기한 없음</Badge>;
    }

    if (dueDate < now) {
      return <Badge variant="danger">마감</Badge>;
    }

    const daysLeft = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 3) {
      return <Badge variant="warning">D-{daysLeft}</Badge>;
    }

    return <Badge variant="success">진행중</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="text-center py-8">
        <p className="text-danger-500 mb-4">{error}</p>
        <Button onClick={fetchAssignments}>다시 시도</Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-neutral-800">과제</h2>
        {userRole === 'TEACHER' && (
          <Button onClick={() => navigate(`/courses/${courseId}/assignments/create`)}>
            새 과제 만들기
          </Button>
        )}
      </div>

      {assignments.length === 0 ? (
        <Card className="text-center py-12">
          <p className="text-neutral-500 mb-4">
            {userRole === 'TEACHER'
              ? '아직 생성된 과제가 없습니다.'
              : '제출할 과제가 없습니다.'}
          </p>
          {userRole === 'TEACHER' && (
            <Button onClick={() => navigate(`/courses/${courseId}/assignments/create`)}>
              과제 만들기
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-4">
          {assignments.map((assignment) => (
            <Card
              key={assignment.id}
              hover
              className="cursor-pointer animate-fade-in"
              onClick={() => navigate(`/courses/${courseId}/assignments/${assignment.id}`)}
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-bold text-neutral-800 flex-1">
                  {assignment.title}
                </h3>
                {getStatusBadge(assignment)}
              </div>

              {assignment.description && (
                <p className="text-sm text-neutral-600 mb-4 line-clamp-2">
                  {assignment.description}
                </p>
              )}

              <div className="flex items-center justify-between text-sm text-neutral-500">
                <div className="flex items-center gap-4">
                  {assignment.dueDate && (
                    <span>마감: {new Date(assignment.dueDate).toLocaleDateString('ko-KR')}</span>
                  )}
                  {assignment.maxScore && (
                    <span>배점: {assignment.maxScore}점</span>
                  )}
                </div>
                {userRole === 'TEACHER' && assignment._count && (
                  <span>{assignment._count.submissions || 0}명 제출</span>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
